/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, Input, AfterViewInit, ElementRef, ChangeDetectorRef} from '@angular/core';
import {DueDateDay} from '../../../../../../model/due-date-day';
import {HolidayDates} from '../../../../../../model/helper/holiday-dates';
declare var moment: any;

@Component({
  selector: 'due-date-content',
  templateUrl: 'due-date-content.component.html',
  styleUrls: ['due-date-content.component.scss']
})
export class DueDateContentComponent implements AfterViewInit {
  @Input() hostElement: HTMLElement;
  @Input() engageDate: Date;
  @Input() turnFrom: number;
  @Input() turnTo: number;

  dayArray: DueDateDay[];

  private top: number = -100000;
  private left: number = -100000;

  constructor(
    private element: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
  }

  ngAfterViewInit(): void {
    this.show();
    this.cdr.detectChanges();
  }

  show(): void {
    if (!this.hostElement)
      return;

    this.getDateList();

    const p = this.positionElements(this.hostElement, this.element.nativeElement.children[0]);
    this.top = p.top;
    this.left = p.left;
  }

  hide(): void {
    this.top = -100000;
    this.left = -100000;
  }

  private getDateList(): void {
    if(!this.engageDate)
      return;

    let date = moment(this.engageDate);
    let days = this.turnTo + 1;
    this.dayArray = [];

    while (days > 0) {
      let dueDateDay: DueDateDay = new DueDateDay();

      if(this.dayArray.length === 0)
        dueDateDay.text = 'Report Engaged';

      dueDateDay.dayNumber = date.unix();
      dueDateDay.dayDisplay = date.format('DD');
      dueDateDay.dayOfWeek = date.format('ddd').toUpperCase();

      if(date.isoWeekday() === 6 || date.isoWeekday() === 7)
        dueDateDay.isWeekend = true;

      if(HolidayDates.isHoliday(date)){
        dueDateDay.isHoliday = true;
        dueDateDay.text = 'Holiday';
      }

      if(date.format('DD/MM/YYYY') === moment().format('DD/MM/YYYY'))
        dueDateDay.isToday = true;

      date = date.add(1, 'days');

      if (date.isoWeekday() !== 6 && date.isoWeekday() !== 7 && !HolidayDates.isHoliday(date))
        days -= 1;

      this.dayArray.push(dueDateDay);
    }
  }

  private positionElements(hostEl: HTMLElement, targetEl: HTMLElement): { top: number, left: number } {
    let hostElPos = this.position(hostEl);
    let targetElWidth = targetEl.offsetWidth;
    let targetElHeight = targetEl.offsetHeight;

    let shiftHeight: any = {
      center: function (): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top: function (): number {
        return hostElPos.top;
      },
      bottom: function (): number {
        return hostElPos.top + hostElPos.height;
      }
    };

    let targetElPos: { top: number, left: number };

    targetElPos = {
      top: shiftHeight['center'](),
      left: hostElPos.left - targetElWidth
    };

    return targetElPos;
  }

  private position(nativeEl: HTMLElement): { width: number, height: number, top: number, left: number } {
    let offsetParentBCR = { top: 0, left: 0 };
    const elBCR = this.offset(nativeEl);
    const offsetParentEl = this.parentOffsetEl(nativeEl);
    if (offsetParentEl !== window.document) {
      offsetParentBCR = this.offset(offsetParentEl);
      offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left
    };
  }

  private offset(nativeEl:any): { width: number, height: number, top: number, left: number } {
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: boundingClientRect.top + (window.pageYOffset || window.document.documentElement.scrollTop),
      left: boundingClientRect.left + (window.pageXOffset || window.document.documentElement.scrollLeft)
    };
  }

  private getStyle(nativeEl: HTMLElement, cssProp: string): string {
    if ((nativeEl as any).currentStyle) // IE
      return (nativeEl as any).currentStyle[cssProp];

    if (window.getComputedStyle)
      return (window.getComputedStyle(nativeEl) as any)[cssProp];

    return (nativeEl.style as any)[cssProp];
  }

  private isStaticPositioned(nativeEl: HTMLElement): boolean {
    return (this.getStyle(nativeEl, 'position') || 'static' ) === 'static';
  }

  private parentOffsetEl(nativeEl: HTMLElement): any {
    let offsetParent: any = nativeEl.offsetParent || window.document;
    while (offsetParent && offsetParent !== window.document && this.isStaticPositioned(offsetParent)) {
      offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || window.document;
  }
}
